import React, { useEffect, useState, useMemo } from "react";
import { Formik, Form } from "formik";
import { object, string } from "yup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers, faPlus, faSearch, faPen, faTimes,
  faSave, faUserCheck, faUserSlash, faSyncAlt,
  faPhone, faEnvelope, faWhatsapp,
} from "@fortawesome/free-solid-svg-icons";
import getAllUsers from "../../components/IssueDetails/getAllUsers";
import addNewOperator from "../../components/Admin/addNewOperator";
import editOperatorInfo from "../../components/Admin/editOperatorInfo";
import getUserDetails from "../../components/Admin/getUserDetails";
import ModalAlert from "../../components/reusable/ModalAlert";

/* ─── Palette ─────────────────────────────────────────────────────────────── */
const C = {
  dark: "#1a3328", mid: "#2d5a3d", bright: "#3d8c5e",
  light: "#e8f5ee", page: "#f4f6f5", border: "#d8e8dc",
  white: "#ffffff", textDk: "#1a2e22", textGr: "#6c757d",
  red: "#c62828", redBg: "#fdecea",
};

/* ─── Validation schemas ──────────────────────────────────────────────────── */
const addSchema = object().shape({
  fullName:      string().nullable().required("Required").matches(/^[a-zA-Z ]*$/, "Text only"),
  emailID:       string().nullable().required("Required").email("Invalid email"),
  mobileNumber:  string().nullable().required("Required").matches(/^\d{10}$/, "10 digits required"),
  whatsappNumber:string().nullable().matches(/^\d{10}$/, "10 digits required"),
  password:      string().nullable().required("Required")
    .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d]{6,15}$/,
      "6–15 chars, upper + lower + number, no special chars"),
  confirmpassword: string().nullable().required("Required")
    .test("match", "Passwords must match", function (v) { return this.parent.password === v; }),
});

const editSchema = object().shape({
  fullName:      string().nullable().required("Required").matches(/^[a-zA-Z ]*$/, "Text only"),
  emailID:       string().nullable().required("Required").email("Invalid email"),
  mobileNumber:  string().nullable().required("Required").matches(/^\d{10}$/, "10 digits required"),
  whatsappNumber:string().nullable().matches(/^\d{10}$/, "10 digits required"),
  password:      string().nullable()
    .test("pwd-format", "6–15 chars, upper + lower + number, no special chars", (v) =>
      !v || /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d]{6,15}$/.test(v)),
  confirmpassword: string().nullable()
    .test("match", "Passwords must match", function (v) {
      if (!this.parent.password) return true;
      return this.parent.password === v;
    }),
});

/* ─── Helpers ─────────────────────────────────────────────────────────────── */
const initials = (name = "") =>
  name.trim().split(/\s+/).map((w) => w[0]).join("").toUpperCase().slice(0, 2) || "?";

const AVATAR_COLORS = ["#2d5a3d","#3b82f6","#d4a853","#7b1fa2","#c62828","#2d7a45","#1e6fc4"];

/* ─── Field component ─────────────────────────────────────────────────────── */
const Field = ({ label, name, type = "text", values, errors, touched, handleChange, handleBlur, disabled }) => (
  <div style={{ marginBottom: "1rem" }}>
    <label style={{
      fontSize: "0.75rem", fontWeight: 600, color: C.textGr,
      display: "block", marginBottom: "0.3rem",
      textTransform: "uppercase", letterSpacing: "0.05em",
    }}>
      {label}
    </label>
    <input
      type={type}
      name={name}
      value={values[name] || ""}
      onChange={handleChange}
      onBlur={handleBlur}
      disabled={disabled}
      style={{
        width: "100%", height: 40, borderRadius: 8,
        border: `1.5px solid ${touched[name] && errors[name] ? C.red : C.border}`,
        padding: "0 0.8rem", fontSize: "0.87rem", outline: "none",
        color: C.textDk, backgroundColor: disabled ? "#f5f5f5" : C.white,
        boxSizing: "border-box",
      }}
    />
    {touched[name] && errors[name] && (
      <div style={{ fontSize: "0.72rem", color: C.red, marginTop: "0.25rem" }}>{errors[name]}</div>
    )}
  </div>
);

/* ─── Right Drawer ────────────────────────────────────────────────────────── */
function OperatorDrawer({ open, mode, operatorData, onClose, onSuccess }) {
  const [apiError, setApiError] = useState("");
  const [saving,   setSaving]   = useState(false);

  const isAdd = mode === "add";

  const initVals = isAdd
    ? { fullName: "", emailID: "", mobileNumber: "", whatsappNumber: "", password: "", confirmpassword: "" }
    : { ...operatorData, password: "", confirmpassword: "" };

  const handleSubmit = async (values) => {
    setSaving(true);
    setApiError("");
    const res = isAdd ? await addNewOperator(values) : await editOperatorInfo(values);
    setSaving(false);
    if (res?.status === 200) { onSuccess(); onClose(); }
    else setApiError(isAdd
      ? "Error adding operator. Check mobile/email for duplicates."
      : "Error updating operator. Please try again.");
  };

  return (
    <>
      {open && (
        <div onClick={onClose} style={{
          position: "fixed", inset: 0, zIndex: 1040,
          backgroundColor: "rgba(0,0,0,0.35)",
        }} />
      )}

      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0,
        width: 420, zIndex: 1050,
        backgroundColor: C.white,
        boxShadow: "-4px 0 28px rgba(0,0,0,0.18)",
        transform: open ? "translateX(0)" : "translateX(100%)",
        transition: "transform 0.28s cubic-bezier(0.4,0,0.2,1)",
        display: "flex", flexDirection: "column",
      }}>
        {/* Drawer header */}
        <div style={{
          backgroundColor: C.dark, padding: "1rem 1.25rem", flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8, backgroundColor: C.mid,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <FontAwesomeIcon icon={faUsers} style={{ color: "#fff", fontSize: "0.8rem" }} />
            </div>
            <span style={{ fontWeight: 700, fontSize: "1rem", color: "#fff" }}>
              {isAdd ? "Add Operator" : "Edit Operator"}
            </span>
          </div>
          <button onClick={onClose} style={{
            background: "rgba(255,255,255,0.12)", border: "none", borderRadius: 7,
            width: 30, height: 30, cursor: "pointer", color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <FontAwesomeIcon icon={faTimes} style={{ fontSize: "0.85rem" }} />
          </button>
        </div>

        {/* Drawer body — Formik */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          <Formik
            key={open ? (isAdd ? "add" : operatorData?.mobileNumber) : "closed"}
            enableReinitialize
            initialValues={initVals}
            validationSchema={isAdd ? addSchema : editSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleChange, handleBlur, handleSubmit: submit }) => (
              <Form style={{ padding: "1.25rem" }}>
                <Field label="Full Name"       name="fullName"       values={values} errors={errors} touched={touched} handleChange={handleChange} handleBlur={handleBlur} />
                <Field label="Mobile Number"   name="mobileNumber"   values={values} errors={errors} touched={touched} handleChange={handleChange} handleBlur={handleBlur} disabled={!isAdd} />
                <Field label="WhatsApp Number" name="whatsappNumber" values={values} errors={errors} touched={touched} handleChange={handleChange} handleBlur={handleBlur} />
                <Field label="Email"           name="emailID"        type="email"    values={values} errors={errors} touched={touched} handleChange={handleChange} handleBlur={handleBlur} />

                {/* Status — edit only */}
                {!isAdd && (
                  <div style={{ marginBottom: "1rem" }}>
                    <label style={{
                      fontSize: "0.75rem", fontWeight: 600, color: C.textGr,
                      display: "block", marginBottom: "0.3rem",
                      textTransform: "uppercase", letterSpacing: "0.05em",
                    }}>
                      Status
                    </label>
                    <select
                      name="status"
                      value={values.status || "active"}
                      onChange={handleChange}
                      style={{
                        width: "100%", height: 40, borderRadius: 8,
                        border: `1.5px solid ${C.border}`, padding: "0 0.8rem",
                        fontSize: "0.87rem", outline: "none", color: C.textDk,
                        backgroundColor: C.white, boxSizing: "border-box",
                      }}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                )}

                <div style={{ height: 1, backgroundColor: C.border, margin: "0.5rem 0 1rem" }} />
                <div style={{ fontSize: "0.72rem", fontWeight: 600, color: C.textGr, marginBottom: "0.75rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  {isAdd ? "Set Password" : "Change Password (leave blank to keep current)"}
                </div>

                <Field label="Password"         name="password"         type="password" values={values} errors={errors} touched={touched} handleChange={handleChange} handleBlur={handleBlur} />
                <Field label="Confirm Password" name="confirmpassword"  type="password" values={values} errors={errors} touched={touched} handleChange={handleChange} handleBlur={handleBlur} />

                {apiError && (
                  <div style={{
                    padding: "0.6rem 0.85rem", borderRadius: 8, marginBottom: "1rem",
                    backgroundColor: C.redBg, color: C.red, fontSize: "0.8rem", fontWeight: 500,
                  }}>
                    {apiError}
                  </div>
                )}

                {/* Footer buttons inside form so Enter submits */}
                <div style={{ display: "flex", gap: 8, marginTop: "0.5rem" }}>
                  <button
                    type="button"
                    onClick={submit}
                    disabled={saving}
                    style={{
                      flex: 1, height: 42, borderRadius: 9, border: "none",
                      backgroundColor: C.mid, color: "#fff",
                      fontSize: "0.87rem", fontWeight: 600,
                      cursor: saving ? "not-allowed" : "pointer",
                      opacity: saving ? 0.7 : 1,
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                    }}
                  >
                    <FontAwesomeIcon icon={faSave} style={{ fontSize: "0.75rem" }} />
                    {saving ? "Saving…" : isAdd ? "Add Operator" : "Save Changes"}
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    style={{
                      height: 42, padding: "0 18px", borderRadius: 9,
                      border: `1.5px solid ${C.border}`, backgroundColor: C.white,
                      color: C.textGr, fontSize: "0.87rem", fontWeight: 600, cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </>
  );
}

/* ─── Operator row ────────────────────────────────────────────────────────── */
function OperatorRow({ operator, index, onEdit }) {
  const [hover, setHover] = useState(false);
  const active = operator.status !== "inactive";
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex", alignItems: "center", gap: "1rem",
        padding: "0.75rem 1rem",
        backgroundColor: hover ? C.light : (index % 2 === 0 ? C.white : "#f8fbf9"),
        borderBottom: `1px solid ${C.border}`,
        transition: "background 0.12s",
      }}
    >
      {/* Avatar */}
      <div style={{
        width: 40, height: 40, borderRadius: "50%", flexShrink: 0,
        backgroundColor: AVATAR_COLORS[index % AVATAR_COLORS.length],
        color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: "0.82rem", fontWeight: 700,
      }}>
        {initials(operator.fullName)}
      </div>

      {/* Name + contact */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: "0.9rem", color: C.textDk }}>{operator.fullName}</div>
        <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginTop: 3, flexWrap: "wrap" }}>
          <span style={{ fontSize: "0.73rem", color: C.textGr, display: "flex", alignItems: "center", gap: 4 }}>
            <FontAwesomeIcon icon={faPhone} style={{ fontSize: "0.6rem" }} />
            {operator.mobileNumber}
          </span>
          {operator.emailID && (
            <span style={{ fontSize: "0.73rem", color: C.textGr, display: "flex", alignItems: "center", gap: 4 }}>
              <FontAwesomeIcon icon={faEnvelope} style={{ fontSize: "0.6rem" }} />
              {operator.emailID}
            </span>
          )}
        </div>
      </div>

      {/* Status badge */}
      <span style={{
        fontSize: "0.7rem", fontWeight: 700, padding: "3px 10px", borderRadius: 10,
        backgroundColor: active ? C.light : C.redBg,
        color: active ? C.bright : C.red,
        whiteSpace: "nowrap", flexShrink: 0,
      }}>
        {active ? "Active" : "Inactive"}
      </span>

      {/* Edit button */}
      <button
        onClick={() => onEdit(operator)}
        style={{
          height: 32, padding: "0 14px", borderRadius: 8,
          border: `1px solid ${C.border}`,
          backgroundColor: hover ? C.white : "transparent",
          color: C.mid, fontSize: "0.78rem", fontWeight: 600,
          cursor: "pointer", display: "flex", alignItems: "center", gap: 5,
          flexShrink: 0, transition: "background 0.12s",
        }}
      >
        <FontAwesomeIcon icon={faPen} style={{ fontSize: "0.62rem" }} />
        Edit
      </button>
    </div>
  );
}

/* ─── Main page ───────────────────────────────────────────────────────────── */
const SignUp = () => {
  const [operators, setOperators] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState("");
  const [drawer,    setDrawer]    = useState({ open: false, mode: "add", operator: null });

  const load = async () => {
    setLoading(true);
    const res = await getAllUsers();
    if (res?.status === 200 && Array.isArray(res.data)) setOperators(res.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() =>
    operators.filter((op) =>
      (op.fullName      || "").toLowerCase().includes(search.toLowerCase()) ||
      (op.mobileNumber  || "").includes(search) ||
      (op.emailID       || "").toLowerCase().includes(search.toLowerCase())
    ), [operators, search]);

  const openAdd  = () => setDrawer({ open: true, mode: "add",  operator: null });
  const openEdit = async (op) => {
    const res = await getUserDetails(op.mobileNumber);
    const data = res?.status === 200 && res.data?.[0] ? { ...res.data[0], password: "", confirmpassword: "" } : { ...op, password: "", confirmpassword: "" };
    setDrawer({ open: true, mode: "edit", operator: data });
  };
  const closeDrawer = () => setDrawer((d) => ({ ...d, open: false }));

  const activeCount   = operators.filter((o) => o.status !== "inactive").length;
  const inactiveCount = operators.length - activeCount;

  return (
    <div style={{ backgroundColor: C.page, minHeight: "calc(100vh - 64px)", display: "flex", flexDirection: "column" }}>

      {/* ── Header ── */}
      <div style={{
        backgroundColor: C.white, padding: "0.85rem 1.5rem", flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        borderBottom: `1px solid ${C.border}`,
        boxShadow: "0 1px 4px rgba(20,50,30,0.06)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 9, backgroundColor: C.light,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <FontAwesomeIcon icon={faUsers} style={{ color: C.mid, fontSize: "0.9rem" }} />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: "1rem", color: C.textDk }}>Operator Management</div>
            <div style={{ fontSize: "0.72rem", color: C.textGr }}>
              {operators.length} operators · {activeCount} active · {inactiveCount} inactive
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={load} style={{
            display: "inline-flex", alignItems: "center", gap: 6, height: 34,
            padding: "0 12px", borderRadius: 8, cursor: "pointer",
            border: `1px solid ${C.border}`, backgroundColor: C.white,
            color: C.textGr, fontSize: "0.8rem", fontWeight: 600,
          }}>
            <FontAwesomeIcon icon={faSyncAlt} style={{ fontSize: "0.72rem" }} /> Refresh
          </button>
          <button onClick={openAdd} style={{
            display: "inline-flex", alignItems: "center", gap: 6, height: 34,
            padding: "0 14px", borderRadius: 8, border: "none", cursor: "pointer",
            backgroundColor: C.mid, color: "#fff",
            fontSize: "0.82rem", fontWeight: 600,
          }}>
            <FontAwesomeIcon icon={faPlus} style={{ fontSize: "0.72rem" }} /> Add Operator
          </button>
        </div>
      </div>

      {/* ── Stat cards ── */}
      <div style={{ display: "flex", gap: "1rem", padding: "1.25rem 1.5rem 0", flexShrink: 0 }}>
        {[
          { icon: faUsers,     label: "Total Operators", value: operators.length, bg: C.light,  color: C.mid   },
          { icon: faUserCheck, label: "Active",          value: activeCount,      bg: "#e8f5e9", color: "#2e7d32" },
          { icon: faUserSlash, label: "Inactive",        value: inactiveCount,    bg: C.redBg,  color: C.red   },
        ].map(({ icon, label, value, bg, color }) => (
          <div key={label} style={{
            backgroundColor: C.white, borderRadius: 10, padding: "0.8rem 1.1rem",
            border: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: "0.75rem",
            boxShadow: "0 2px 8px rgba(20,50,30,0.05)",
          }}>
            <div style={{
              width: 36, height: 36, borderRadius: "50%", backgroundColor: bg,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <FontAwesomeIcon icon={icon} style={{ color, fontSize: "0.88rem" }} />
            </div>
            <div>
              <div style={{ fontSize: "1.5rem", fontWeight: 800, color: C.textDk, lineHeight: 1 }}>{value}</div>
              <div style={{ fontSize: "0.7rem", color: C.textGr, marginTop: 2 }}>{label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Content ── */}
      <div style={{ flex: 1, padding: "1.25rem 1.5rem", overflowY: "auto" }}>
        <div style={{
          backgroundColor: C.white, borderRadius: 12,
          border: `1px solid ${C.border}`,
          boxShadow: "0 2px 12px rgba(20,50,30,0.05)",
          overflow: "hidden",
        }}>
          {/* Toolbar */}
          <div style={{
            display: "flex", alignItems: "center", gap: "0.75rem",
            padding: "0.85rem 1rem", borderBottom: `1px solid ${C.border}`,
            backgroundColor: "#f8faf9",
          }}>
            <div style={{ flex: 1, position: "relative" }}>
              <FontAwesomeIcon icon={faSearch} style={{
                position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
                color: C.textGr, fontSize: "0.75rem",
              }} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, mobile or email…"
                style={{
                  width: "100%", height: 36, borderRadius: 8,
                  border: `1.5px solid ${C.border}`, paddingLeft: 30, paddingRight: 10,
                  fontSize: "0.83rem", outline: "none", color: C.textDk,
                  backgroundColor: C.white, boxSizing: "border-box",
                }}
              />
            </div>
            <span style={{ fontSize: "0.78rem", color: C.textGr, whiteSpace: "nowrap" }}>
              {filtered.length} operator{filtered.length !== 1 ? "s" : ""}
            </span>
          </div>

          {/* List */}
          {loading ? (
            <div style={{ textAlign: "center", padding: "4rem", color: C.textGr, fontSize: "0.9rem" }}>
              Loading operators…
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: "center", padding: "4rem", color: C.textGr }}>
              <FontAwesomeIcon icon={faUsers} style={{ fontSize: "2.5rem", color: C.border, display: "block", marginBottom: "0.75rem" }} />
              <div style={{ fontWeight: 600 }}>{search ? "No operators match your search" : "No operators yet"}</div>
              {!search && <div style={{ fontSize: "0.82rem", marginTop: 5 }}>Click "Add Operator" to get started</div>}
            </div>
          ) : (
            filtered.map((op, i) => (
              <OperatorRow key={op._id || op.mobileNumber} operator={op} index={i} onEdit={openEdit} />
            ))
          )}
        </div>
      </div>

      {/* ── Right Drawer ── */}
      <OperatorDrawer
        open={drawer.open}
        mode={drawer.mode}
        operatorData={drawer.operator}
        onClose={closeDrawer}
        onSuccess={() => { closeDrawer(); load(); }}
      />
    </div>
  );
};

export default SignUp;
